import type {
  IWidgetConfig,
  IWidgetCorridor,
  IUserData,
} from "../shared/session/interfaces/session";
import type { IEstimateOption } from "../services/api";

/** Country → ISO 4217 currency (display / quote denomination helper). */
export const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  AR: "ARS",
  CO: "COP",
  BR: "BRL",
  CL: "CLP",
  PE: "PEN",
  GT: "GTQ",
  UY: "UYU",
  DO: "DOP",
  HN: "HNL",
  SV: "USD",
  NI: "NIO",
  CR: "CRC",
  PA: "PAB",
  EC: "USD",
  PY: "PYG",
  BO: "BOB",
  VE: "VES",
  ES: "EUR",
  DE: "EUR",
  FR: "EUR",
};

/**
 * Catalog funding methods (UPPER_SNAKE). Mirrors the canonical list in
 * `raas-widgets-backend/src/catalog/constants/catalog.constants.ts`.
 */
export const FUNDING_METHODS = [
  "DEBIT_CARD",
  "CREDIT_CARD",
  "ACH",
  "RTP",
  "WALLET",
  "CASH_DEPOSIT",
  "BANK_BALANCE",
] as const;
export type FundingMethod = (typeof FUNDING_METHODS)[number];

/**
 * Catalog payout methods (UPPER_SNAKE). Mirrors the canonical list in
 * `raas-widgets-backend/src/catalog/constants/catalog.constants.ts`.
 */
export const PAYOUT_METHODS = [
  "CASH_PICKUP",
  "BANK_ACCOUNT",
  "PUSH_TO_CARD",
  "MOBILE_WALLET",
  "HOME_DELIVERY",
] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

/**
 * Display labels for catalog funding methods (UPPER_SNAKE → human-friendly).
 */
export const FUNDING_METHOD_DISPLAY_NAMES: Record<FundingMethod, string> = {
  DEBIT_CARD: "Debit card",
  CREDIT_CARD: "Credit card",
  ACH: "Bank account (ACH)",
  RTP: "Bank account (RTP)",
  WALLET: "Wallet",
  CASH_DEPOSIT: "Cash deposit",
  BANK_BALANCE: "Bank balance",
};

/**
 * Display labels for catalog payout methods (UPPER_SNAKE → human-friendly).
 */
export const PAYOUT_METHOD_DISPLAY_NAMES: Record<PayoutMethod, string> = {
  BANK_ACCOUNT: "Bank account",
  CASH_PICKUP: "Cash pickup",
  PUSH_TO_CARD: "To debit card",
  MOBILE_WALLET: "Mobile wallet",
  HOME_DELIVERY: "Home delivery",
};

/**
 * Maps catalog funding method codes to the legacy UI category used by
 * payment-method-selection components: CARD | ACCOUNT | CASH | OTHER. Lets
 * existing flavor-specific payment selectors keep working while we transition
 * to native UPPER_SNAKE chips.
 */
export type FundingCategory = "CARD" | "ACCOUNT" | "CASH" | "OTHER";

export const FUNDING_METHOD_CATEGORY: Record<FundingMethod, FundingCategory> = {
  DEBIT_CARD: "CARD",
  CREDIT_CARD: "CARD",
  ACH: "ACCOUNT",
  RTP: "ACCOUNT",
  BANK_BALANCE: "ACCOUNT",
  WALLET: "OTHER",
  CASH_DEPOSIT: "CASH",
};

/**
 * Classify any funding method string into a UI category (CARD / ACCOUNT /
 * CASH / OTHER). Accepts both the canonical catalog vocabulary and the
 * legacy lowercase trio (`card` / `bank_account` / `cash`) so that operation
 * history rendered by the widget keeps working across the cutover.
 */
export function categoryFromFundingMethod(
  method: string | undefined | null,
): FundingCategory | undefined {
  if (!method) return undefined;
  const upper = method.toUpperCase();
  if (upper in FUNDING_METHOD_CATEGORY) {
    return FUNDING_METHOD_CATEGORY[upper as FundingMethod];
  }
  switch (method) {
    case "card":
      return "CARD";
    case "bank_account":
      return "ACCOUNT";
    case "cash":
      return "CASH";
    default:
      return undefined;
  }
}

export function formatFundingMethod(method: string): string {
  return FUNDING_METHOD_DISPLAY_NAMES[method as FundingMethod] ?? method;
}

export function formatPayoutMethod(method: string): string {
  return PAYOUT_METHOD_DISPLAY_NAMES[method as PayoutMethod] ?? method;
}

/**
 * Destination country hinted by pre-filled payout data on the claim response.
 */
export function getPayoutDestinationCountry(
  userData?: IUserData | null,
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  return pm.bankAccount?.country?.toUpperCase();
}

function findCorridor(
  corridors: IWidgetCorridor[],
  originCountryCode: string,
  destinationCountryCode: string,
): IWidgetCorridor | undefined {
  return corridors.find(
    (c) =>
      c.originCountryCode === originCountryCode &&
      c.destinationCountryCode === destinationCountryCode,
  );
}

/**
 * Picks the (origin, destination) route the widget should operate against.
 * Resolution order:
 *   1. `defaultCorridor` from the claim `config` (link-level or partner default).
 *   2. A corridor whose destination matches the pre-filled payout country
 *      (`userData.paymentMethod.bankAccount.country`).
 *   3. The single corridor when only one is enabled.
 *   4. The first corridor (deterministic fallback).
 */
export function getActiveCorridor(
  config: IWidgetConfig | undefined | null,
  userData?: IUserData | null,
): IWidgetCorridor | undefined {
  if (!config?.corridors?.length) return undefined;

  const corridors = config.corridors;
  const payoutDestination = getPayoutDestinationCountry(userData);

  if (config.defaultCorridor) {
    const { originCountryCode, destinationCountryCode } = config.defaultCorridor;
    const match = findCorridor(
      corridors,
      originCountryCode,
      destinationCountryCode,
    );
    if (match) return match;
  }

  if (payoutDestination) {
    const matches = corridors.filter(
      (c) => c.destinationCountryCode === payoutDestination,
    );
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) {
      const usOrigin = matches.find((c) => c.originCountryCode === "US");
      return usOrigin ?? matches[0];
    }
  }

  if (corridors.length === 1) return corridors[0];

  return corridors[0];
}

export interface ActiveRoute {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  corridor: IWidgetCorridor | undefined;
}

/**
 * Resolves countries and currencies for the active corridor using the claim
 * response (`config` + `userData`).
 */
export function resolveActiveRoute(
  config: IWidgetConfig | undefined | null,
  userData?: IUserData | null,
): ActiveRoute {
  const corridor = getActiveCorridor(config, userData);
  const sourceCountry = corridor?.originCountryCode ?? "US";
  const destinationCountry = corridor?.destinationCountryCode ?? "MX";
  const sourceCurrency =
    config?.sourceCurrency ?? COUNTRY_CURRENCY[sourceCountry] ?? "USD";
  const destinationCurrency =
    config?.destinationCurrency ??
    COUNTRY_CURRENCY[destinationCountry] ??
    "USD";

  return {
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    corridor,
  };
}

/**
 * Convenience: returns the active corridor's funding methods, or [] when no
 * corridor is configured.
 */
export function getActiveFundingMethods(
  config: IWidgetConfig | undefined | null,
  userData?: IUserData | null,
): string[] {
  return getActiveCorridor(config, userData)?.fundingMethods ?? [];
}

/**
 * Convenience: returns the active corridor's payout methods, or [] when no
 * corridor is configured.
 */
export function getActivePayoutMethods(
  config: IWidgetConfig | undefined | null,
  userData?: IUserData | null,
): string[] {
  return getActiveCorridor(config, userData)?.payoutMethods ?? [];
}

/**
 * Maps the widget's legacy payment type (`CARD` | `BANK` | `CASH`) to a
 * canonical catalog funding method (UPPER_SNAKE). Used as the default funding
 * method when the pre-quote has not yet resolved a concrete SKU.
 */
export function mapPaymentTypeToMethod(
  type: string | undefined | null,
): FundingMethod | null {
  switch (type) {
    case "CARD":
      return "DEBIT_CARD";
    case "BANK":
      return "ACH";
    case "CASH":
      return "CASH_DEPOSIT";
    default:
      return null;
  }
}

/**
 * Resolves the canonical payout method (UPPER_SNAKE) the transfer must be
 * priced against. In m2m the payout is fixed by the claim link, so we prefer
 * the explicit `userData.paymentMethod.payoutMethod` and fall back to mapping
 * the legacy `type` discriminator.
 */
export function resolvePayoutMethod(
  userData?: IUserData | null,
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.payoutMethod) return pm.payoutMethod;
  switch (pm.type) {
    case "bank_account":
    case "BANK_ACCOUNT":
      return "BANK_ACCOUNT";
    case "cash":
    case "CASH_PICKUP":
      return "CASH_PICKUP";
    case "card":
    case "PUSH_TO_CARD":
      return "PUSH_TO_CARD";
    default:
      return undefined;
  }
}

/**
 * Resolves the canonical payer code the transfer is locked to (e.g. `ELEKTRA`
 * for cash pickup, `BBVA` for a bank account), or `undefined` when the claim
 * does not pin a specific payer.
 */
export function resolvePayerCode(
  userData?: IUserData | null,
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.type === "cash") return pm.cash?.payer ?? pm.cash?.provider;
  if (pm.type === "bank_account") {
    return pm.bankAccount?.payer ?? pm.bankAccount?.bankCode;
  }
  return undefined;
}

/**
 * Narrows a pre-quote's options to the capabilities that satisfy the claim's
 * fixed payout method (and payer, when pinned). When filtering by payer yields
 * nothing (e.g. the payer code doesn't match any option), the payout-method
 * filter result is returned so the screen still has quotable options.
 */
export function filterPreQuoteOptions(
  options: IEstimateOption[] | undefined | null,
  payoutMethod: string | undefined,
  payerCode: string | undefined,
): IEstimateOption[] {
  const all = options ?? [];
  let result = all;
  if (payoutMethod) {
    const byPayout = all.filter((o) => o.payoutMethod === payoutMethod);
    // Unknown/mismatched claim payout must not wipe every SKU — otherwise the
    // UI can show a payment method with no capabilityKey and never bind a quote.
    if (byPayout.length > 0) result = byPayout;
  }
  if (payerCode) {
    const target = payerCode.toUpperCase();
    const byPayer = result.filter(
      (o) => (o.payerCode ?? "").toUpperCase() === target,
    );
    if (byPayer.length > 0) result = byPayer;
  }
  return result;
}

/**
 * Distinct funding methods available across a set of (already filtered)
 * pre-quote options, in catalog order.
 */
export function availableFundingMethods(
  options: IEstimateOption[],
): FundingMethod[] {
  const seen = new Set(options.map((o) => o.fundingMethod));
  return FUNDING_METHODS.filter((m) => seen.has(m));
}

/**
 * Distinct UI funding categories (CARD / ACCOUNT / CASH / OTHER) available
 * across a set of (already filtered) pre-quote options.
 */
export function availableFundingCategories(
  options: IEstimateOption[],
): Set<FundingCategory> {
  return new Set(
    options
      .map((o) => categoryFromFundingMethod(o.fundingMethod))
      .filter((c): c is FundingCategory => Boolean(c)),
  );
}

/**
 * Picks the cheapest (lowest `sendAmount`) option whose funding method maps to
 * the given UI category, from an already-filtered option list.
 */
export function optionForCategory(
  options: IEstimateOption[],
  category: FundingCategory,
): IEstimateOption | undefined {
  return options
    .filter((o) => categoryFromFundingMethod(o.fundingMethod) === category)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}
